import { NavLink } from "react-router-dom";
import { useData } from "../data/DataContext";

/* ------- SVG icons (stroke-based; inherit currentColor) ------- */

const IconUser = () => (
  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 opacity-90">
    <path
      d="M10 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-6 6a6 6 0 0 1 12 0H4Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconGrid = () => (
  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 opacity-90">
    <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const IconList = () => (
  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 opacity-90">
    <path d="M4 5h12M4 10h12M4 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconLayers = () => (
  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 opacity-90">
    <path d="M10 3 3 7l7 4 7-4-7-4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path
      d="M3 13l7 4 7-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconPeople = () => (
  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 opacity-90">
    <path
      d="M7 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm6 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM2 16a5 5 0 0 1 10 0H2Zm11 0a4 4 0 0 1 1-2.65"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconReport = () => (
  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 opacity-90">
    <path
      d="M5 3h7l4 4v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm7 0v4h4M7 12h6M7 15h4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCog = () => (
  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 opacity-90">
    <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10 2v2m0 12v2M2 10h2m12 0h2M4.2 4.2l1.4 1.4m8.8 8.8 1.4 1.4M4.2 15.8l1.4-1.4m8.8-8.8 1.4-1.4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

/* ------- helpers ------- */

function initials(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ------- nav item ------- */

interface ItemProps {
  to: string;
  end?: boolean;
  label: string;
  icon: React.ReactNode;
  badge?: string;
}

function NavItem({ to, end, label, icon, badge }: ItemProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "relative flex items-center gap-[11px] rounded-sm px-[14px] py-[10px] text-[13.5px] font-medium transition select-none",
          isActive
            ? "bg-[rgb(215_173_95/0.12)] text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r-[2px] before:bg-brass-light before:content-['']"
            : "text-[#D7D1BF] hover:bg-white/5 hover:text-white",
        ].join(" ")
      }
    >
      {icon}
      <span>{label}</span>
      {badge ? (
        <span className="ml-auto rounded-[10px] bg-[rgb(215_173_95/0.18)] px-[7px] py-[2px] font-mono text-[10px] font-medium text-brass-light">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

/* ------- sidebar ------- */

export function Sidebar() {
  const { me } = useData();
  const role = me.role;
  // Leadership-facing nav is Admin/Exec/CLT only. User (self-service) sees just
  // My objectives - plus "People I cover" if they have at least one coveree.
  // Viewer fallback sees the same as User-with-no-covers (My objectives only).
  const isStaff = role === "Admin" || role === "Exec" || role === "CLT";
  const canCover = isStaff || (role === "User" && me.covers.length > 0);
  // Admin panel: Admin/Exec get the full view; CLT get a filtered view limited
  // to people they cover. Handled inside AdminPage.
  const showAdmin = isStaff;

  return (
    <aside className="flex w-[248px] flex-col bg-nymr-navy pt-[22px] pb-[18px] text-[#E8E4D7] print:hidden">
      {/* Brand */}
      <div className="mb-[14px] border-b border-white/10 px-[22px] pb-[22px]">
        <div className="inline-flex items-center gap-3">
          <div className="grid h-[34px] w-[34px] place-items-center rounded-full bg-brass font-display text-[15px] font-semibold text-nymr-navy-ink shadow-[inset_0_0_0_2px_rgba(255,255,255,0.15)]">
            NY
          </div>
          <div className="flex flex-col leading-[1.1]">
            <span className="font-display text-[16px] font-medium text-white tracking-[0.01em]">
              Objectives
            </span>
            <span className="mt-[3px] font-mono text-[10px] uppercase tracking-[0.14em] text-brass-light">
              NYMR &middot; 2026/7
            </span>
          </div>
        </div>
      </div>

      {/* Working on */}
      <div className="px-[14px] pt-[14px] pb-[4px] font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
        Working on
      </div>
      <nav className="flex flex-col gap-[2px] px-[10px]">
        <NavItem to="/" end label="My objectives" icon={<IconUser />} />
        {isStaff && <NavItem to="/team" label="Team dashboard" icon={<IconGrid />} />}
        {isStaff && <NavItem to="/objectives" label="All objectives" icon={<IconList />} />}
        {isStaff && <NavItem to="/categories" label="Categories" icon={<IconLayers />} />}
        {canCover && <NavItem to="/cover" label="People I cover" icon={<IconPeople />} />}
      </nav>

      {/* Share */}
      {isStaff && (
        <>
          <div className="mt-[14px] px-[14px] pt-[14px] pb-[4px] font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
            Share
          </div>
          <nav className="flex flex-col gap-[2px] px-[10px]">
            <NavItem to="/reports" label="Reports & export" icon={<IconReport />} />
            {showAdmin ? <NavItem to="/admin" label="Admin" icon={<IconCog />} /> : null}
          </nav>
        </>
      )}

      {/* Footer */}
      <div className="mt-auto border-t border-white/10 px-[18px] pt-[14px] pb-[6px]">
        <div className="flex items-center gap-[11px]">
          <div className="grid h-[34px] w-[34px] place-items-center rounded-full bg-gradient-to-br from-brass-light to-brass text-[13px] font-semibold text-nymr-navy-ink">
            {initials(me.displayName)}
          </div>
          <div className="min-w-0 leading-[1.2]">
            <div className="truncate text-[13px] font-medium text-white">
              {me.displayName || "Signed in"}
            </div>
            <div className="mt-[2px] font-mono text-[10px] uppercase tracking-[0.08em] text-white/50">
              {role.toUpperCase()}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
